import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import i18n from '~/lib/i18n'

const SUPPORTED_LOCALES = ['es', 'gl', 'en', 'de', 'fr', 'it', 'nl', 'pt']

export const Route = createFileRoute('/$locale')({
  // Sync language synchronously before any child route renders — avoids
  // a flash of English content when locale is 'es', 'fr', etc.
  //
  // If the segment isn't a valid locale (e.g. /sign-in) treat it as a
  // path and redirect to /en/{segment} (B-new-1008 — unified default locale,
  // was 'es').
  beforeLoad: ({ params }) => {
    if (!SUPPORTED_LOCALES.includes(params.locale)) {
      throw redirect({ href: `/en/${params.locale}` })
    }
    if (i18n.language !== params.locale) {
      // Fire-and-forget: do NOT await — awaiting inside beforeLoad causes
      // TanStack Router to see a concurrent navigation, producing the
      // "Error in route match: /$locale/.../<url>" doubled-path warning.
      i18n.changeLanguage(params.locale)
    }
  },
  component: LocaleLayout,
})

function LocaleLayout() {
  const { locale } = Route.useParams()

  // WCAG 3.1.1 Language of Page — keep <html lang> in sync with the
  // route's locale so screen readers pick the correct TTS voice. The
  // index.html ships with lang="en"; without this effect every non-en
  // locale would be read with an English voice (blocker for a
  // multilingual app). See docs/13-compliance/WCAG-AUDIT-2026-05-31.md
  // § S-02.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement.lang !== locale) {
      document.documentElement.lang = locale
    }
  }, [locale])

  return <Outlet />
}
